import SavePoint, { CreateSavePointSource } from "@/utils/savePoint";
import { getPublicImageUrl } from "@/utils/imageUtils";
import { logger } from "@/utils/logger";
import { determineRestorationBehavior } from "@/hooks/create-page/utils/restorationHelpers";
import {
  buildFormDataFromUrlParams,
  hasFormDataChanges,
  UrlParamValues,
} from "@/hooks/create-page/utils/urlParamHelpers";
import { parseDesignSpecSnapshot } from "@/lib/agent/designSpecSnapshot";
import { restoreDesignDraft } from "@/lib/designer/restoreDesignDraft";
import { CreateFormData } from "@/components/create/types";
import { CreatePageState } from "@/stores/createPageStore";
import {
  hasMeaningfulFormData,
  hasMeaningfulSerializedFormState,
} from "@/hooks/create-page/utils/formStateHelpers";
import { Ticket } from "./ticketService";

// SessionStorage key for tracking restoration completion
const RESTORATION_COMPLETE_KEY = "create.restoration.complete";
const CREATE_VARIATIONS_STORAGE_KEY = "createPageVariations";
let signOutNavigationPending = false;

function getRestorationKey(savePointTimestamp: number): string {
  return `${RESTORATION_COMPLETE_KEY}.${savePointTimestamp}`;
}

function isRestorationComplete(savePointTimestamp: number): boolean {
  if (typeof window === "undefined" || !window.sessionStorage) {
    return false;
  }
  try {
    const key = getRestorationKey(savePointTimestamp);
    return sessionStorage.getItem(key) === "true";
  } catch {
    return false;
  }
}

function markRestorationComplete(savePointTimestamp: number): void {
  if (typeof window === "undefined" || !window.sessionStorage) {
    return;
  }
  try {
    const key = getRestorationKey(savePointTimestamp);
    sessionStorage.setItem(key, "true");
  } catch {
    // Ignore storage errors
  }
}

function hasUploadedImageReferences(
  formData: Partial<CreateFormData> | undefined
): boolean {
  return !!(
    (formData?.uploadedFrontImages &&
      formData.uploadedFrontImages.length > 0) ||
    (formData?.uploadedBackImages && formData.uploadedBackImages.length > 0) ||
    (formData?.uploadedFrontImageUrls &&
      formData.uploadedFrontImageUrls.length > 0) ||
    (formData?.uploadedBackImageUrls &&
      formData.uploadedBackImageUrls.length > 0)
  );
}

function getTicketRenderSnapshots(
  frontImage: string | null,
  backImage: string | null,
  frontPersistedSpec: unknown,
  backPersistedSpec: unknown
) {
  // The mutable `tickets.design_spec` is the latest quote/current contract,
  // not render provenance. Only the independently versioned side columns may
  // be attached to an image; missing or invalid legacy data fails closed.
  return {
    frontDesignSpec: frontImage
      ? parseDesignSpecSnapshot(frontPersistedSpec)
      : null,
    backDesignSpec: backImage
      ? parseDesignSpecSnapshot(backPersistedSpec)
      : null,
  };
}

export function clearRestorationFlags(): void {
  if (typeof window === "undefined" || !window.sessionStorage) {
    return;
  }
  try {
    const keys: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith(RESTORATION_COMPLETE_KEY)) {
        keys.push(key);
      }
    }
    keys.forEach(key => sessionStorage.removeItem(key));
  } catch {
    // Ignore storage errors
  }
}

export interface RestorationResult {
  type:
    | "ticket_prefill"
    | "gallery_prefill"
    | "savepoint"
    | "url_params"
    | "none";
  data: any;
  shouldRestore: boolean;
  targetStep?: number;
  toastMessage?: string;
  ticketId?: string | null;
  isForBackGeneration?: boolean;
  isModificationMode?: boolean;
}

export interface SaveStateParams {
  data: any;
  source: CreateSavePointSource;
  intentId?: string | null;
  ownerUserId?: string | null;
}

export interface RestoreStateParams {
  // Priority 1: Ticket/Gallery prefill
  ticketData?: Ticket | null;
  ticketId?: string | null;
  modifyParam?: string | null;
  galleryDesignParam?: string | null;
  isForBackGeneration?: boolean;

  // Priority 2: SavePoint restoration
  session?: any;
  authLoading?: boolean;
  shouldRestore?: boolean;
  restoreIntentId?: string | null;

  // Priority 3: URL params
  searchParamValues?: UrlParamValues;

  // Current state (to check if restoration is needed)
  currentState: CreatePageState;
}

/**
 * Unified form persistence service
 * Centralizes all form persistence logic with priority handling
 */
export const FormPersistenceService = {
  /**
   * True only for the lifetime of the page that initiated account sign-out.
   * A module flag is intentional: the subsequent full navigation creates a
   * fresh runtime, while the current page's `pagehide` handler can see it and
   * avoid recreating the state we just cleared.
   */
  isSignOutNavigationPending(): boolean {
    return signOutNavigationPending;
  },

  clearStateForSignOut(): void {
    signOutNavigationPending = true;
    this.clearState();
  },

  /**
   * Clears the singleton draft owned by this browsing context while preserving
   * opaque, URL-scoped handoffs that another tab may still be completing.
   */
  clearCanonicalState(): void {
    SavePoint.clear(undefined, "canonical");
    clearRestorationFlags();
    try {
      if (typeof window !== "undefined" && window.localStorage) {
        localStorage.removeItem(CREATE_VARIATIONS_STORAGE_KEY);
      }
    } catch {
      // Browser storage is best effort.
    }
  },

  /**
   * Save form state
   */
  saveState({ data, source, intentId, ownerUserId }: SaveStateParams): void {
    logger.debug("FormPersistenceService.saveState: Called", {
      component: "FormPersistenceService",
      source,
      hasData: !!data,
      dataType: typeof data,
      currentStep: (data as any)?.currentStep,
      hasFormData: !!(data as any)?.formData,
      hasImageUrl: !!(data as any)?.imageUrl,
      hasImagePath: !!(data as any)?.imagePath,
    });
    if (intentId && ownerUserId) {
      SavePoint.saveFormState(data, source, intentId, ownerUserId);
    } else if (intentId) SavePoint.saveFormState(data, source, intentId);
    else SavePoint.saveFormState(data, source);
    logger.debug("FormPersistenceService.saveState: Completed", {
      component: "FormPersistenceService",
      source,
    });
  },

  /**
   * Restore form state with priority logic
   * Priority: Ticket/Gallery prefill > SavePoint > URL params > Default
   */
  restoreState(params: RestoreStateParams): RestorationResult {
    const {
      ticketData,
      ticketId,
      modifyParam,
      galleryDesignParam,
      isForBackGeneration = false,
      session,
      authLoading = false,
      shouldRestore: urlShouldRestore,
      restoreIntentId,
      searchParamValues,
      currentState,
    } = params;

    // Priority 1: Ticket/Gallery Prefill (highest priority)
    if (ticketData && (ticketId || modifyParam)) {
      const frontPath: string | null =
        ticketData.image_url || ticketData.image_path || null;
      const backPath: string | null =
        ticketData.back_image_url || ticketData.back_image_path || null;
      const frontDisplay = frontPath ? getPublicImageUrl(frontPath) : null;
      const backDisplay = backPath ? getPublicImageUrl(backPath) : null;
      const { frontDesignSpec, backDesignSpec } = getTicketRenderSnapshots(
        frontDisplay,
        backDisplay,
        ticketData.front_render_design_spec,
        ticketData.back_render_design_spec
      );

      // The ticket's current contract owns the editable draft. Its legacy
      // columns cannot carry mood, occasion, date or the full constraints.
      // Keep the independent render snapshots above attached to their images.
      const currentContract = parseDesignSpecSnapshot(ticketData.design_spec);
      const currentDraft = currentContract
        ? restoreDesignDraft(currentContract)
        : null;
      const formPayload: Partial<CreateFormData> = {
        prompt: ticketData.prompt || "",
        color: ticketData.color || "",
        length: ticketData.dress_length || ticketData.length || "",
        sleeve: ticketData.sleeve_style || ticketData.sleeve || "",
        neckline: ticketData.neckline_style || ticketData.neckline || "",
        waistline: ticketData.waistline_style || ticketData.waistline || "",
        silhouette: ticketData.silhouette_fit || ticketData.silhouette || "",
        fabric: ticketData.fabric_texture || ticketData.fabric || "",
        backPrompt: ticketData.back_prompt || "",
        ...currentDraft?.formData,
      };

      if (isForBackGeneration) {
        // For back image generation: set ticketId, go to step 3, lock front design
        return {
          type: "ticket_prefill",
          shouldRestore: true,
          data: {
            formData: formPayload,
            designBrief: currentDraft?.brief ?? null,
            currentStep: 3,
            imageUrl: frontDisplay,
            imagePath: frontPath,
            frontDesignSpec,
            backImageUrl: backDisplay,
            backImagePath: backPath,
            backDesignSpec,
            originalImageUrl: frontDisplay,
            originalImagePath: frontPath,
            originalFrontDesignSpec: frontDesignSpec,
            originalBackImageUrl: backDisplay,
            originalBackImagePath: backPath,
            originalBackDesignSpec: backDesignSpec,
            ticketId: ticketId,
            currentView: "front" as const,
            originalPresetType: ticketData.preset_type || null,
          },
          ticketId: ticketId || null,
          isForBackGeneration: true,
        };
      } else if (ticketId && !modifyParam) {
        // A bare ticket link reopens the saved design for review. Only the
        // explicit `step=3` route is a command to skip that decision and enter
        // back design; treating every `ticket_id` as either back-generation or
        // modification made ordinary restores land in the wrong workflow.
        return {
          type: "ticket_prefill",
          shouldRestore: true,
          data: {
            formData: formPayload,
            designBrief: currentDraft?.brief ?? null,
            currentStep: 2,
            imageUrl: frontDisplay,
            imagePath: frontPath,
            frontDesignSpec,
            backImageUrl: backDisplay,
            backImagePath: backPath,
            backDesignSpec,
            originalImageUrl: frontDisplay,
            originalImagePath: frontPath,
            originalFrontDesignSpec: frontDesignSpec,
            originalBackImageUrl: backDisplay,
            originalBackImagePath: backPath,
            originalBackDesignSpec: backDesignSpec,
            ticketId,
            currentView: "front" as const,
            originalPresetType: ticketData.preset_type || null,
          },
          ticketId,
        };
      } else {
        // For modification mode: set ticketId to null, go to step 4
        return {
          type: "ticket_prefill",
          shouldRestore: true,
          data: {
            formData: formPayload,
            designBrief: currentDraft?.brief ?? null,
            currentStep: 4,
            imageUrl: frontDisplay,
            imagePath: frontPath,
            frontDesignSpec,
            backImageUrl: backDisplay,
            backImagePath: backPath,
            backDesignSpec,
            originalImageUrl: frontDisplay,
            originalImagePath: frontPath,
            originalFrontDesignSpec: frontDesignSpec,
            originalBackImageUrl: backDisplay,
            originalBackImagePath: backPath,
            originalBackDesignSpec: backDesignSpec,
            ticketId: null,
            currentView: "front" as const,
            originalPresetType: ticketData.preset_type || null,
            showModifyInput: true,
          },
          ticketId: null,
          isModificationMode: true,
        };
      }
    }

    // Gallery design prefill
    if (galleryDesignParam) {
      try {
        const parsed = JSON.parse(galleryDesignParam);
        const frontPath: string | null = parsed.original_image_path || null;
        const backPath: string | null = parsed.back_image_path || null;
        const frontDisplay = getPublicImageUrl(parsed.image_url || frontPath);
        const backDisplay = getPublicImageUrl(
          parsed.back_image_url || backPath
        );
        const { frontDesignSpec, backDesignSpec } = getTicketRenderSnapshots(
          frontDisplay,
          backDisplay,
          parsed.front_render_design_spec,
          parsed.back_render_design_spec
        );

        const formPayload: Partial<CreateFormData> = {
          prompt: parsed.prompt || "",
          color: parsed.color || "",
          length: parsed.length || "",
          sleeve: parsed.sleeve || "",
          neckline: parsed.neckline || "",
          waistline: parsed.waistline || "",
          silhouette: parsed.silhouette || "",
          fabric: parsed.fabric || "",
          backPrompt: parsed.back_prompt || "",
        };

        return {
          type: "gallery_prefill",
          shouldRestore: true,
          data: {
            formData: formPayload,
            currentStep: 4,
            imageUrl: frontDisplay,
            imagePath: frontPath,
            frontDesignSpec,
            backImageUrl: backDisplay,
            backImagePath: backPath,
            backDesignSpec,
            originalImageUrl: frontDisplay,
            originalImagePath: frontPath,
            originalFrontDesignSpec: frontDesignSpec,
            originalBackImageUrl: backDisplay,
            originalBackImagePath: backPath,
            originalBackDesignSpec: backDesignSpec,
            ticketId: null,
            currentView: "front" as const,
            originalPresetType: parsed.preset_type || null,
          },
          isModificationMode: true,
        };
      } catch {
        // Invalid gallery design param, continue to next priority
      }
    }

    // Priority 2: SavePoint Restoration (only if no ticket/gallery prefill)
    if (!modifyParam && !galleryDesignParam) {
      const savePoint = SavePoint.getLatestSavePoint(restoreIntentId);
      logger.debug("FormPersistenceService.restoreState: Checking SavePoint", {
        component: "FormPersistenceService",
        hasSavePoint: !!savePoint,
        savePointSource: savePoint?.source,
        savePointAge: savePoint ? Date.now() - savePoint.savedAt : null,
        urlShouldRestore,
        modifyParam: !!modifyParam,
        galleryDesignParam: !!galleryDesignParam,
      });

      if (savePoint) {
        // Check if we've already restored this specific save point
        const alreadyRestored = isRestorationComplete(savePoint.savedAt);
        logger.debug(
          "FormPersistenceService.restoreState: Restoration status",
          {
            component: "FormPersistenceService",
            source: savePoint.source,
            savedAt: savePoint.savedAt,
            alreadyRestored,
            urlShouldRestore,
          }
        );

        // Auth and checkout returns are authoritative when restore_form=true.
        // Skip the early return so a briefly hydrated previous-account dress
        // cannot win over the anonymous save point being adopted.
        // BUT: Only clear the flag if it's the first time we're seeing this save point
        // with urlShouldRestore=true. We track this with a separate flag to prevent loops.
        if (
          alreadyRestored &&
          (savePoint.source === "auth_redirect" ||
            savePoint.source === "credit_purchase_redirect") &&
          urlShouldRestore
        ) {
          const key = getRestorationKey(savePoint.savedAt);
          const clearedFlagKey = `create.restoration.cleared.${savePoint.savedAt}`;

          // Check if we've already cleared this flag for this save point
          const alreadyCleared =
            typeof window !== "undefined" && window.sessionStorage
              ? sessionStorage.getItem(clearedFlagKey) === "true"
              : false;

          if (!alreadyCleared) {
            logger.debug(
              "FormPersistenceService.restoreState: Credit purchase redirect with restore_form param - clearing flag to allow restoration",
              {
                component: "FormPersistenceService",
                source: savePoint.source,
                savedAt: savePoint.savedAt,
                urlShouldRestore,
              }
            );
            // Clear the flag ONCE so restoration can proceed
            // The restoration logic will mark it complete again after restoration
            if (typeof window !== "undefined" && window.sessionStorage) {
              try {
                if (sessionStorage.getItem(key)) {
                  sessionStorage.removeItem(key);
                  // Mark that we've cleared this flag to prevent clearing it again
                  sessionStorage.setItem(clearedFlagKey, "true");
                  logger.debug(
                    "FormPersistenceService.restoreState: Cleared restoration flag",
                    {
                      component: "FormPersistenceService",
                      key,
                    }
                  );
                }
              } catch {
                // Ignore storage errors
              }
            }
            // Continue to restoration logic below - don't return early
          } else {
            // We already cleared this flag and tried to restore - don't try again
            logger.debug(
              "FormPersistenceService.restoreState: Already cleared flag for this save point, skipping",
              {
                component: "FormPersistenceService",
                source: savePoint.source,
                savedAt: savePoint.savedAt,
              }
            );
            return { type: "none", shouldRestore: false, data: null };
          }
        } else if (alreadyRestored) {
          // Already restored, skip
          logger.debug(
            "FormPersistenceService.restoreState: Already restored, skipping",
            {
              component: "FormPersistenceService",
              source: savePoint.source,
              savedAt: savePoint.savedAt,
            }
          );
          return { type: "none", shouldRestore: false, data: null };
        }

        // For auth_redirect and credit_purchase_redirect, we need auth to finish loading and a session
        const requiresSession =
          savePoint.source === "auth_redirect" ||
          savePoint.source === "credit_purchase_redirect";

        logger.debug(
          "FormPersistenceService.restoreState: Session requirements",
          {
            component: "FormPersistenceService",
            source: savePoint.source,
            requiresSession,
            authLoading,
            hasSession: !!session,
          }
        );

        // Wait for auth to finish loading before making session-dependent decisions
        if (requiresSession && authLoading) {
          // Auth still loading - return false to signal caller should wait
          // The effect will re-run when authLoading changes
          logger.debug(
            "FormPersistenceService.restoreState: Auth still loading, waiting",
            {
              component: "FormPersistenceService",
              source: savePoint.source,
            }
          );
          return { type: "none", shouldRestore: false, data: null };
        }

        if (requiresSession && !session) {
          // Auth finished but no session - cannot restore auth-dependent save points
          logger.debug(
            "FormPersistenceService.restoreState: No session, cannot restore",
            {
              component: "FormPersistenceService",
              source: savePoint.source,
              requiresSession,
            }
          );
          return { type: "none", shouldRestore: false, data: null };
        }

        // A paid checkout return is a scoped, account-owned capability. Never
        // let a missing id fall back to the singleton draft, and never expose
        // a saved design after the browser has switched to another account.
        if (
          savePoint.source === "credit_purchase_redirect" &&
          (!restoreIntentId ||
            savePoint.intentId !== restoreIntentId ||
            !savePoint.ownerUserId ||
            savePoint.ownerUserId !== session?.user?.id)
        ) {
          logger.warn(
            "FormPersistenceService.restoreState: Refused unowned credit checkout restore",
            {
              component: "FormPersistenceService",
              hasRestoreIntentId: !!restoreIntentId,
              intentMatches: savePoint.intentId === restoreIntentId,
              hasOwner: !!savePoint.ownerUserId,
              ownerMatches: savePoint.ownerUserId === session?.user?.id,
            }
          );
          return { type: "none", shouldRestore: false, data: null };
        }

        // Explicit auth/checkout returns can proceed even if this save point
        // was marked complete before navigation.
        const isComplete = isRestorationComplete(savePoint.savedAt);
        const shouldProceed =
          !isComplete ||
          ((savePoint.source === "auth_redirect" ||
            savePoint.source === "credit_purchase_redirect") &&
            urlShouldRestore);

        if (shouldProceed) {
          // For auth_redirect and credit_purchase_redirect, verify form state is actually populated
          const needsVerification = requiresSession;

          if (needsVerification) {
            const hasFormData = hasMeaningfulFormData(currentState);

            logger.debug(
              "FormPersistenceService.restoreState: Verifying form state",
              {
                component: "FormPersistenceService",
                source: savePoint.source,
                hasFormData,
                hasPrompt: !!currentState.form.data?.prompt?.trim(),
                hasImageUrl: !!currentState.generation.images.front.url,
                hasImagePath: !!currentState.generation.images.front.path,
                hasUploadedImage: !!hasUploadedImageReferences(
                  currentState.form.data
                ),
              }
            );

            if (!hasFormData && savePoint.data) {
              const hasSavePointData = hasMeaningfulSerializedFormState(
                savePoint.data
              );

              logger.debug(
                "FormPersistenceService.restoreState: Checking save point data",
                {
                  component: "FormPersistenceService",
                  source: savePoint.source,
                  hasSavePointData,
                  hasSavePointPrompt: !!savePoint.data.formData?.prompt?.trim(),
                  hasSavePointImageUrl: !!savePoint.data.imageUrl,
                  hasSavePointImagePath: !!savePoint.data.imagePath,
                  hasSavePointUploadedImage: !!hasUploadedImageReferences(
                    savePoint.data.formData
                  ),
                }
              );

              if (!hasSavePointData) {
                // No data to restore, clear save point
                logger.debug(
                  "FormPersistenceService.restoreState: No save point data, clearing",
                  {
                    component: "FormPersistenceService",
                    source: savePoint.source,
                  }
                );
                SavePoint.clear(restoreIntentId);
                // Continue to next priority
              } else {
                // Clear the flag to allow restoration
                logger.debug(
                  "FormPersistenceService.restoreState: Clearing restoration flag to allow restore",
                  {
                    component: "FormPersistenceService",
                    source: savePoint.source,
                  }
                );
                const key = getRestorationKey(savePoint.savedAt);
                if (typeof window !== "undefined" && window.sessionStorage) {
                  try {
                    sessionStorage.removeItem(key);
                  } catch {
                    // Ignore storage errors
                  }
                }
              }
            } else if (hasFormData) {
              // An explicit auth/checkout return owns the page even when the
              // global store briefly contains another dress from the account.
              if (
                (savePoint.source === "auth_redirect" ||
                  savePoint.source === "credit_purchase_redirect") &&
                urlShouldRestore
              ) {
                logger.debug(
                  "FormPersistenceService.restoreState: Explicit redirect allowing restoration despite form data",
                  {
                    component: "FormPersistenceService",
                    source: savePoint.source,
                    urlShouldRestore,
                    hasFormData,
                  }
                );
                // Don't mark as complete - let the restoration decision logic handle it
                // This ensures restoration happens when returning from checkout
              } else {
                // Already has form data, mark as complete
                logger.debug(
                  "FormPersistenceService.restoreState: Already has form data, marking complete",
                  {
                    component: "FormPersistenceService",
                    source: savePoint.source,
                    urlShouldRestore,
                  }
                );
                markRestorationComplete(savePoint.savedAt);
                return { type: "none", shouldRestore: false, data: null };
              }
            }
          } else {
            // For auto_save and before_unload, check if form already has data
            const hasFormData =
              currentState.form.data?.prompt?.trim() ||
              currentState.generation.images.front.url ||
              currentState.generation.images.front.path ||
              currentState.agent.designBrief ||
              hasUploadedImageReferences(currentState.form.data);

            if (hasFormData) {
              markRestorationComplete(savePoint.savedAt);
              return { type: "none", shouldRestore: false, data: null };
            } else if (savePoint.data) {
              // Clear the flag to allow restoration
              const key = getRestorationKey(savePoint.savedAt);
              if (typeof window !== "undefined" && window.sessionStorage) {
                try {
                  sessionStorage.removeItem(key);
                } catch {
                  // Ignore storage errors
                }
              }
            }
          }

          const age = Date.now() - savePoint.savedAt;
          const hasImages = !!(
            savePoint.data.imageUrl || savePoint.data.imagePath
          );
          // `restore_form` is a handoff marker for auth/checkout save points,
          // not permission to resurrect an unrelated stale auto-save. Recent
          // auto-saves still restore through their normal 30-second rule.
          const explicitRedirectRestore = Boolean(
            urlShouldRestore &&
            (savePoint.source === "auth_redirect" ||
              savePoint.source === "credit_purchase_redirect")
          );

          // Determine if we should restore based on source and conditions
          const restoration = determineRestorationBehavior(
            explicitRedirectRestore,
            savePoint.source,
            age,
            savePoint.data.currentStep,
            hasImages
          );

          logger.debug(
            "FormPersistenceService.restoreState: Restoration decision",
            {
              component: "FormPersistenceService",
              source: savePoint.source,
              urlShouldRestore: urlShouldRestore || false,
              age,
              savedStep: savePoint.data.currentStep,
              hasImages,
              shouldRestore: restoration.shouldRestore,
              targetStep: restoration.targetStep,
            }
          );

          if (restoration.shouldRestore) {
            // Mark restoration as complete BEFORE returning
            markRestorationComplete(savePoint.savedAt);

            // Prepare restore data with target step
            const restoreData = {
              ...savePoint.data,
              currentStep: restoration.targetStep,
            };

            logger.info("Restoring form state from SavePoint", {
              component: "FormPersistenceService",
              source: savePoint.source,
              savedStep: savePoint.data.currentStep,
              targetStep: restoration.targetStep,
              hasFormData: !!restoreData.formData,
              hasImageUrl: !!restoreData.imageUrl,
              hasImagePath: !!restoreData.imagePath,
              hasVariations: !!(
                restoreData.frontVariations || restoreData.backVariations
              ),
            });

            return {
              type: "savepoint",
              shouldRestore: true,
              data: restoreData,
              targetStep: restoration.targetStep,
              toastMessage: restoration.toastMessage,
            };
          } else {
            logger.debug(
              "FormPersistenceService.restoreState: Restoration decision says not to restore",
              {
                component: "FormPersistenceService",
                source: savePoint.source,
                restorationDecision: restoration,
              }
            );
          }
        }
      }
    }

    // Priority 3: URL Parameters (only if no prefill or restoration)
    if (searchParamValues && !currentState.ui.modification.isModificationMode) {
      const {
        prompt: promptParam,
        length: lengthParam,
        sleeve: sleeveParam,
        neckline: necklineParam,
        silhouette: silhouetteParam,
        fabric: fabricParam,
        preset: presetParam,
        color: colorParam,
      } = searchParamValues;

      const hasUrlParams =
        promptParam ||
        lengthParam ||
        sleeveParam ||
        necklineParam ||
        silhouetteParam ||
        fabricParam ||
        presetParam ||
        colorParam;

      if (hasUrlParams) {
        const newFormData = buildFormDataFromUrlParams(
          searchParamValues,
          currentState.form.data
        );

        // Only update if form data is different from current state
        if (hasFormDataChanges(currentState.form.data, newFormData)) {
          return {
            type: "url_params",
            shouldRestore: true,
            data: newFormData,
          };
        }
      }
    }

    // No restoration needed
    return { type: "none", shouldRestore: false, data: null };
  },

  /**
   * Clear all saved state
   */
  clearState(): void {
    SavePoint.clear();
    clearRestorationFlags();
    try {
      if (typeof window !== "undefined" && window.localStorage) {
        localStorage.removeItem(CREATE_VARIATIONS_STORAGE_KEY);
      }
    } catch {
      // Browser storage is best effort; stale data is still ownership-checked
      // by the server if private mode prevents cleanup.
    }
  },
};
